import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Adg4Button } from "@/components/adg4/button/src";
import { Picture } from "@/components/wpl/image/src";
import { Video } from "@/components/wpl/video/src";
import { Row } from "@/libraries/wpl/foundations/src";

import {
    ArrowPencilPointRight,
    ArrowPencilPointLeft
} from "../sections-v2/illustrations";

export const AtlassianIntelligence = () => {
    const intl = useIntl();
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();
    // edit these constant values to move the group of arrows all together

    return (
        <section
            id="atlassian-intelligence"
            css={[sectionStyles, animationStyles]}
            ref={scrollRef}
            className={sectionIsVisible ? "is-visible" : ""}
        >
            <Row layout="12">
                <div>
                    <div className="content-wrapper" css={wrapperStyles}>
                        <div css={containerStyles}>
                            <div
                                className="background"
                                css={backgroundStyles}
                            />
                            <div className="branding" css={brandingStyles}>
                                <img
                                    src="//wac-cdn.atlassian.com/misc-assets/webp-images/Atlassian-Intelligence-icon.svg"
                                    alt=""
                                    css={logoStyles}
                                />
                                <p className="h6">Atlassian Intelligence</p>
                            </div>
                            <h2 css={headerStyles}>
                                Weave AI into all of your work
                            </h2>
                            <div css={css({ position: "relative" })}>
                                <LeftArrows />
                                <RightArrows />
                                <Video
                                    videoLink="//wac-cdn.atlassian.com/misc-assets/webp-images/Atlassian-intelligence.webm"
                                    videoType="webm"
                                    className="video"
                                    // !!!TODO: REPLACE PLACEHOLDER IMAGE
                                    videoPreviewImage="https://picsum.photos/969/499"
                                >
                                    <Picture
                                        className="fallback-img--desktop"
                                        image={{
                                            // !!!TODO: REPLACE PLACEHOLDER IMAGE
                                            src: "https://picsum.photos/969/499",
                                            alt: "",
                                            aspectRatio: "none"
                                        }}
                                    />
                                </Video>
                            </div>
                            <Adg4Button variant="secondary" color="black">
                                {intl.formatMessage({
                                    id: "HomePageTemplateV2.AtlassianIntelligenceCtaButton",
                                    defaultMessage:
                                        "Explore Atlassian Intelligence"
                                })}
                            </Adg4Button>
                        </div>
                    </div>
                </div>
            </Row>
        </section>
    );
};

const LeftArrows = () => {
    return (
        <div className="left-arrows">
            <ArrowPencilPointLeft
                className="arrow-left1"
                color="#101214"
                rotate={192}
            />
            <ArrowPencilPointLeft
                className="arrow-left2"
                color="#BC5DF2"
                rotate={222}
            />
            <ArrowPencilPointLeft
                className="arrow-left3"
                color="#1868DB"
                rotate={199}
            />
            <ArrowPencilPointLeft
                className="arrow-left4"
                color="#fff"
                rotate={219}
            />
            <ArrowPencilPointLeft
                className="arrow-left5"
                color="#101214"
                rotate={196}
            />
            <ArrowPencilPointLeft
                className="arrow-left6"
                color="#1868DB"
                rotate={199}
            />
        </div>
    );
};

const RightArrows = () => {
    return (
        <div className="right-arrows">
            <ArrowPencilPointRight
                className="arrow-right1"
                color="#101214"
                rotate={138}
            />
            <ArrowPencilPointRight
                className="arrow-right2"
                color="#1868DB"
                rotate={139}
            />
            <ArrowPencilPointRight
                className="arrow-right3"
                color="#fff"
                rotate={127}
            />
            <ArrowPencilPointRight
                className="arrow-right4"
                color="#82B536"
                rotate={114}
            />
            <ArrowPencilPointRight
                className="arrow-right5"
                color="#101214"
                rotate={138}
            />
        </div>
    );
};
const sectionStyles = css({
    "paddingBlockEnd": "32px",
    "overflow": "hidden",

    ".row-layout": {
        position: "relative"
    },

    "svg": {
        position: "absolute",
        animationDuration: "1s",
        animationIterationCount: 1,
        animationFillMode: "forwards"
    },

    "&.is-visible": {
        svg: {
            animationName: "arrowIn"
        }
    }
});

const wrapperStyles = css({
    "display": "flex",
    "alignItems": "center",
    "flexDirection": "column",
    "position": "relative",
    "p, h2": {
        margin: 0,
        color: "#fff !important",
        zIndex: 3
    },

    ".left-arrows, .right-arrows": {
        position: "absolute",
        height: "100%",
        width: "100%",
        insetBlockStart: 0,

        zIndex: 2,

        svg: {
            [media.below.sm]: {
                height: "9px",
                width: "24px"
            },
            [media.only.sm]: {
                height: "17px",
                width: "50px"
            }
        }
    },
    ".right-arrows": {
        insetInlineStart: "1%",
        [media.below.md]: {
            insetBlockStart: "-33%",
            insetInlineStart: "-15%"
        }
    },
    ".left-arrows": {
        insetInlineStart: "-1%",
        [media.below.md]: {
            insetBlockStart: "-76%",
            insetInlineStart: "14%"
        }
    }
});

const headerStyles = css({
    [media.below.sm]: {
        maxWidth: "80%"
    }
});

const containerStyles = css({
    "display": "flex",
    "alignItems": "center",
    "flexDirection": "column",
    "gap": "32px",
    "maxWidth": "969px",
    "paddingBlockStart": "66px",
    "position": "relative",
    "textAlign": "center",

    ".video": {
        boxShadow:
            "0px 0px 1px 0px rgba(9, 30, 66, 0.12), 0px 0px 8px 0px rgba(9, 30, 66, 0.16)",
        borderRadius: "10px"
    }
});

const backgroundStyles = css({
    position: "absolute",
    insetInlineStart: "-100px",
    insetBlockStart: 0,
    width: "calc(100% + 200px)",
    height: "74%",
    backgroundColor: "#09326C",
    zIndex: "-1",
    clipPath: "polygon(330% 0,0 0,0 100%,0 95%)"
});

const brandingStyles = css({
    display: "flex",
    alignItems: "center",
    gap: "12px"
});

const logoStyles = css({
    backgroundColor: "#1868DB",
    padding: "4.9px"
});

const animationStyles = css({
    "@keyframes arrow-left1": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "-15%"
        },
        to: {
            insetInlineStart: "-0.5%",
            insetBlockStart: "65%"
        }
    },
    "@keyframes arrow-left2": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "-60%"
        },
        to: {
            insetInlineStart: "-5%",
            insetBlockStart: "49%"
        }
    },
    "@keyframes arrow-left3": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "-40%"
        },
        to: {
            insetInlineStart: "-12%",
            insetBlockStart: "58%"
        }
    },
    "@keyframes arrow-left4": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "-25%"
        },
        to: {
            insetInlineStart: "-13%",
            insetBlockStart: "44%"
        }
    },
    "@keyframes arrow-left5": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "-40%"
        },
        to: {
            insetInlineStart: "-21%",
            insetBlockStart: "48%"
        }
    },
    "@keyframes arrow-left6": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "-15%"
        },
        to: {
            insetInlineStart: "-25%",
            insetBlockStart: "33%"
        }
    },
    "@keyframes arrow-right1": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "-20%"
        },
        to: {
            insetInlineEnd: "-6%",
            insetBlockStart: "18%"
        }
    },
    "@keyframes arrow-right2": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "-40%"
        },
        to: {
            insetInlineEnd: "-14%",
            insetBlockStart: "5%"
        }
    },
    "@keyframes arrow-right3": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "-30%"
        },
        to: {
            insetInlineEnd: "-12%",
            insetBlockStart: "-9%"
        }
    },
    "@keyframes arrow-right4": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "-50%"
        },
        to: {
            insetInlineEnd: "-17%",
            insetBlockStart: "-24%"
        }
    },
    "@keyframes arrow-right5": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "-20%"
        },
        to: {
            insetInlineEnd: "-26%",
            insetBlockStart: "-17%"
        }
    },

    "&.is-visible": {
        svg: {
            "&.arrow-left1": {
                animationName: "arrow-left1"
            },
            "&.arrow-left2": {
                animationName: "arrow-left2"
            },
            "&.arrow-left3": {
                animationName: "arrow-left3"
            },
            "&.arrow-left4": {
                animationName: "arrow-left4"
            },
            "&.arrow-left5": {
                animationName: "arrow-left5"
            },
            "&.arrow-left6": {
                animationName: "arrow-left6"
            },

            "&.arrow-right1": {
                animationName: "arrow-right1"
            },
            "&.arrow-right2": {
                animationName: "arrow-right2"
            },
            "&.arrow-right3": {
                animationName: "arrow-right3"
            },
            "&.arrow-right4": {
                animationName: "arrow-right4"
            },
            "&.arrow-right5": {
                animationName: "arrow-right5"
            }
        }
    }
});
